import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type { HealthDistrictProjectConfigurationDto, UserDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useProjectAppBrandingConfig } from "@/providers/ProjectAppBrandingConfigProvider";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type HealthDistrictFormValues = Omit<HealthDistrictProjectConfigurationDto, "apiUserId"> & {
  apiUser: UserDto | undefined;
};

export function HealthDistrictPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const { appBrandingConfig } = useProjectAppBrandingConfig();
  const integrationQueries = useIntegrationQueries();
  const userQueries = useUserQueries();

  const {
    data: config,
    isFetching: isLoadingConfig,
    error: configError,
  } = useQuery(integrationQueries.healthDistrictConfiguration());
  const {
    data: adminUsers,
    isFetching: isLoadingAdminUsers,
    error: adminUsersError,
  } = useQuery(userQueries.listAdmins());
  const mutation = integrationMutations.useEditHealthDistrictConfiguration();

  if (appBrandingConfig.id !== "healthDistrict" || !hasPermission((x) => x.integrations.canManageHealthDistrict)) {
    return <ErrorPage status={404} />;
  }

  if (configError || adminUsersError) {
    return <ErrorPage error={configError || adminUsersError} />;
  }

  if (isLoadingConfig || isLoadingAdminUsers) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.health-district.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <HealthDistrictForm
        defaultValues={{
          enabled: config?.enabled || false,
          baseUrl: config?.baseUrl || "",
          tokenUrl: config?.tokenUrl || "",
          clientId: config?.clientId || "",
          clientSecret: config?.clientSecret || "",
          username: config?.username || "",
          password: config?.password || "",
          apiUser: !config?.apiUserId || !adminUsers ? undefined : adminUsers.find((u) => u.id === config.apiUserId),
        }}
        adminUsers={adminUsers || []}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

interface HealthDistrictFormProps {
  defaultValues: Partial<HealthDistrictFormValues>;
  adminUsers: UserDto[];
  isSubmitting: boolean;
  onSubmit: (data: HealthDistrictProjectConfigurationDto) => void;
}

function HealthDistrictForm({
  defaultValues,
  adminUsers,
  isSubmitting,
  onSubmit,
}: HealthDistrictFormProps): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<HealthDistrictFormValues>({ defaultValues });
  const enabled = useWatch({ control: formMethods.control, name: "enabled" });

  function handleSubmit(formValues: HealthDistrictFormValues) {
    onSubmit({ ...formValues, apiUserId: formValues.apiUser?.id });
  }

  return (
    <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
      <Form.Container title={t("page.integration.health-district.title")}>
        <Form.Body>
          <FormCheckbox<HealthDistrictFormValues, "enabled">
            id="enabled"
            name="enabled"
            label={t("common.action.enable")}
            onChange={(e) => {
              if (enabled && !e.target.checked) {
                formMethods.clearErrors();
              }
            }}
          />

          <FormField
            label={t("page.integration.health-district.form.base-url")}
            required
            tooltip={t("page.integration.health-district.form.base-url.tooltip")}
          >
            <FormInput<HealthDistrictFormValues, "baseUrl">
              id="baseUrl"
              name="baseUrl"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.health-district.form.base-url")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField
            label={t("page.integration.health-district.form.token-url")}
            required
            tooltip={t("page.integration.health-district.form.token-url.tooltip")}
          >
            <FormInput<HealthDistrictFormValues, "tokenUrl">
              id="tokenUrl"
              name="tokenUrl"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.health-district.form.token-url")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.health-district.form.client-id")} required>
            <FormInput<HealthDistrictFormValues, "clientId">
              id="clientId"
              name="clientId"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.health-district.form.client-id")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.health-district.form.client-secret")} required>
            <FormInput<HealthDistrictFormValues, "clientSecret">
              id="clientSecret"
              name="clientSecret"
              type="password"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.health-district.form.client-secret")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.health-district.form.username")} required>
            <FormInput<HealthDistrictFormValues, "username">
              id="username"
              name="username"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.health-district.form.username")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.health-district.form.password")} required>
            <FormInput<HealthDistrictFormValues, "password">
              id="password"
              name="password"
              type="password"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.health-district.form.password")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.health-district.form.api-user")} required>
            <div className="max-w-search-wide">
              <FormSelect<HealthDistrictFormValues, UserDto>
                items={adminUsers || []}
                keySelector={(user) => user.id}
                renderOption={(user) => `${user.firstName} ${user.lastName}`}
                name="apiUser"
                rules={{
                  validate: {
                    required: (value, formValues) => {
                      if (formValues.enabled && !value) {
                        return t("components.form.error.required", {
                          inputName: t("page.integration.health-district.form.api-user"),
                        });
                      }
                    },
                  },
                }}
                disabled={isSubmitting || !enabled}
              />
            </div>
          </FormField>
        </Form.Body>

        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
